import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import Dropdown from '../components/Dropdown';
import PermissionGate from '../components/PermissionGate';
import { canExportMeezan } from '../utils/reportPermissions';
import { responseErrorMessage } from '../utils/responseError';
import * as XLSX from 'xlsx';
import { monthNames } from '../constants';
import { companyToday } from '../utils/dateUtils';

// The company's current month: a payroll month, whatever zone the browser is in.
const getCurrentMonthYear = () => {
  const [year, month] = companyToday().split('-').map(Number);
  return { month, year };
};

const NewMeezanBankTransferSheet = () => {
  const [month, setMonth] = useState(getCurrentMonthYear().month);
  const [year, setYear] = useState(getCurrentMonthYear().year);
  const [salaryReports, setSalaryReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadName, setDownloadName] = useState('');
  const [sheetRows, setSheetRows] = useState([]);

  useEffect(() => {
    // Only the latest month/year may fill the report picker: a late response for the
    // month that was showing before would otherwise list that month's salary sheets
    // under this one, and the transfer sheet would be generated from the wrong month.
    let cancelled = false;
    const fetchReports = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/reports?type=salary_sheet&month=${month}&year=${year}`);
        if (cancelled) return;
        setSalaryReports(res.data.data || []);
      } catch (err) {
        if (cancelled) return;
        setError('Failed to fetch salary sheet reports');
        setSalaryReports([]);
      }
      setLoading(false);
    };
    fetchReports();
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const handleGenerate = async () => {
    if (!selectedReportId) {
      setError('Please select a salary sheet report to reference.');
      return;
    }
    setError('');
    setLoading(true);
    setDownloadUrl('');
    setDownloadName('');
    setSheetRows([]);
    try {
      // Add cache-busting timestamp param
      const timestamp = Date.now();
      
      // Use the backend domain for API calls in production
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://backend-hrms.bytecorp.io' 
        : '';
      const url = `${baseUrl}/api/reports/${selectedReportId}/export-meezan-bank-transfer?t=${timestamp}`;
      console.log('Making request to:', url);
      console.log('Environment:', process.env.NODE_ENV);
      console.log('Base URL:', baseUrl);
      
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (!res.ok) {
        setError(await responseErrorMessage(res, 'Failed to generate Meezan Bank Transfer Sheet'));
        setLoading(false);
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      setDownloadUrl(downloadUrl);
      setDownloadName(`meezan-bank-transfer-${year}-${String(month).padStart(2, '0')}.xlsx`);
      // Parse Excel file
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      setSheetRows(rows);
    } catch (err) {
      setError('Failed to generate Meezan Bank Transfer Sheet');
    }
    setLoading(false);
  };

  return (
    <div className="w-full mx-auto px-0 py-6">
      <h2 className="text-2xl font-bold mb-4">Generate Meezan Bank Transfer Sheet</h2>
      {error && <div className="mb-4 text-red-600">{error}</div>}
      <div className="flex flex-wrap gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium">Month</label>
          <Dropdown value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="reports-field-month">
            {monthNames.map((name, i) => (
              <option key={i+1} value={i+1}>{name}</option>
            ))}
          </Dropdown>
        </div>
        <div>
          <label className="block text-sm font-medium">Year</label>
          <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} onWheel={(e) => e.target.blur()} data-testid="reports-field-year" className="border rounded px-2 py-1 w-24" />
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Select Salary Sheet Report</label>
        {loading ? <div>Loading reports...</div> : (
          <Dropdown
            value={selectedReportId}
            onChange={e => setSelectedReportId(e.target.value)}
            data-testid="reports-field-salary-report"
          >
            <option value="">Select a report</option>
            {salaryReports.map(r => (
              <option key={r.id} value={r.id}>
                {monthNames[r.month - 1]} {r.year} (Created: {new Date(r.created_at).toLocaleDateString()})
              </option>
            ))}
          </Dropdown>
        )}
      </div>
      <button
        data-testid="reports-generate"
        className="px-6 py-2 bg-primary-600 text-white rounded font-semibold disabled:opacity-50"
        onClick={handleGenerate}
        disabled={loading || !selectedReportId}
      >
        {loading ? 'Generating...' : 'Generate'}
      </button>
      {downloadUrl && (
        <div className="mt-6">
          <div className="mb-2 text-green-700">Meezan Bank Transfer Sheet generated successfully!</div>
          <a
            href={downloadUrl}
            download={downloadName}
            className="px-4 py-2 bg-green-600 text-white rounded font-semibold"
          >
            Download Excel
          </a>
          {sheetRows.length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {sheetRows[0].map((col, i) => (
                        <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sheetRows.slice(1).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {row.map((cell, j) => {
                          // Check if this is the account number column (first column)
                          const isAccountNumberColumn = j === 0;
                          const isNumericCell = typeof cell === 'number' || (typeof cell === 'string' && !isNaN(cell) && cell !== '');
                          
                          return (
                            <td key={j} className={`px-6 py-4 whitespace-nowrap${isNumericCell && !isAccountNumberColumn ? ' text-right' : ''}`}>
                              {isAccountNumberColumn ? cell : (isNumericCell ? Number(cell).toLocaleString() : cell)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// The transfer sheet is account numbers and salaries: it always needs salaries:read_all.
const GatedNewMeezanBankTransferSheet = () => (
  <PermissionGate
    check={canExportMeezan}
    message="You do not have permission to export bank transfer sheets"
    redirectTo="/reports/new"
  >
    <NewMeezanBankTransferSheet />
  </PermissionGate>
);

export default GatedNewMeezanBankTransferSheet;
